// src/modules/Legal/Terms.js
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../contexts/LanguageContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 24px 80px;

  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #111;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: #444;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0;
  padding-left: 24px;

  li {
    font-size: 16px;
    line-height: 1.7;
    color: #444;
    margin-bottom: 8px;
  }
`;

const ContactInfo = styled.div`
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;

  p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: #111;
    text-decoration: underline;
  }
`;

export const Terms = () => {
  const { language } = useTranslation();

  // Conteúdo em português
  const contentPT = {
    title: 'Termos de Uso',
    lastUpdated: 'Última atualização: Dezembro de 2024',
    sections: [
      {
        title: '1. Aceitação dos Termos',
        content: 'Ao acessar e utilizar o site da Vega Robotics (vegarobotics.com.br) e seus subdomínios, você concorda com estes Termos de Uso. Se você não concordar com algum termo, por favor, não utilize nosso site.'
      },
      {
        title: '2. Descrição dos Serviços',
        content: 'A Vega Robotics oferece soluções de tecnologia industrial, incluindo:',
        list: [
          'Plataformas de Inteligência Artificial Industrial',
          'Sistemas de Visão Computacional',
          'Serviços de Engenharia sob Demanda (CADPro)',
          'Consultoria em Indústria 4.0',
          'Agentes de IA para automação de processos (PROAgentes.ai)'
        ]
      },
      {
        title: '3. Uso do Site',
        content: 'Você concorda em utilizar este site apenas para fins legais e de maneira que não infrinja os direitos de terceiros. É proibido:',
        list: [
          'Usar o site para qualquer propósito ilegal ou não autorizado',
          'Tentar obter acesso não autorizado a sistemas ou redes',
          'Transmitir vírus, malware ou código malicioso',
          'Coletar informações de outros usuários sem consentimento',
          'Interferir no funcionamento do site'
        ]
      },
      {
        title: '4. Propriedade Intelectual',
        content: 'Todo o conteúdo do site, incluindo textos, imagens, logotipos, gráficos, vídeos e software, é de propriedade da Vega Robotics ou de seus licenciadores e está protegido por leis de direitos autorais e propriedade intelectual. Não é permitida a reprodução, distribuição ou modificação sem autorização prévia.'
      },
      {
        title: '5. Conteúdo do Blog',
        content: 'Os artigos publicados em nosso blog têm caráter informativo e educacional. Embora nos esforcemos para fornecer informações precisas e atualizadas, não garantimos a completude ou exatidão do conteúdo. As opiniões expressas são de responsabilidade dos autores.'
      },
      {
        title: '6. Links para Sites de Terceiros',
        content: 'Nosso site pode conter links para sites de terceiros. Não somos responsáveis pelo conteúdo, políticas de privacidade ou práticas desses sites. O acesso a sites externos é por sua conta e risco.'
      },
      {
        title: '7. Limitação de Responsabilidade',
        content: 'A Vega Robotics não se responsabiliza por danos diretos, indiretos, incidentais ou consequenciais resultantes do uso ou impossibilidade de uso do site. O site é fornecido "como está", sem garantias de qualquer tipo.'
      },
      {
        title: '8. Modificações nos Termos',
        content: 'Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. As alterações entram em vigor imediatamente após sua publicação no site. O uso continuado do site após as modificações constitui aceitação dos novos termos.'
      },
      {
        title: '9. Lei Aplicável',
        content: 'Estes Termos de Uso são regidos pelas leis da República Federativa do Brasil. Qualquer disputa será submetida ao foro da Comarca de Campinas, Estado de São Paulo.'
      },
      {
        title: '10. Contato',
        content: 'Para dúvidas sobre estes Termos de Uso, entre em contato:'
      }
    ],
    contact: {
      company: 'Vega Robotics',
      email: 'contato@vegarobotics.com.br',
      address: 'Av. Alan Turing, 345 - Sala 11, Campinas - SP, 13083-898'
    }
  };

  // Conteúdo em inglês
  const contentEN = {
    title: 'Terms of Use',
    lastUpdated: 'Last updated: December 2024',
    sections: [
      {
        title: '1. Acceptance of Terms',
        content: 'By accessing and using the Vega Robotics website (vegarobotics.com.br) and its subdomains, you agree to these Terms of Use. If you do not agree with any term, please do not use our website.'
      },
      {
        title: '2. Description of Services',
        content: 'Vega Robotics offers industrial technology solutions, including:',
        list: [
          'Industrial Artificial Intelligence Platforms',
          'Computer Vision Systems',
          'On-Demand Engineering Services (CADPro)',
          'Industry 4.0 Consulting',
          'AI Agents for process automation (PROAgentes.ai)'
        ]
      },
      {
        title: '3. Use of the Website',
        content: 'You agree to use this website only for lawful purposes and in a manner that does not infringe on the rights of others. It is prohibited to:',
        list: [
          'Use the website for any illegal or unauthorized purpose',
          'Attempt to gain unauthorized access to systems or networks',
          'Transmit viruses, malware, or malicious code',
          'Collect information from other users without consent',
          'Interfere with the operation of the website'
        ]
      },
      {
        title: '4. Intellectual Property',
        content: 'All website content, including texts, images, logos, graphics, videos, and software, is owned by Vega Robotics or its licensors and is protected by copyright and intellectual property laws. Reproduction, distribution, or modification without prior authorization is not permitted.'
      },
      {
        title: '5. Blog Content',
        content: 'Articles published on our blog are for informational and educational purposes. While we strive to provide accurate and up-to-date information, we do not guarantee the completeness or accuracy of the content. The opinions expressed are the responsibility of the authors.'
      },
      {
        title: '6. Links to Third-Party Websites',
        content: 'Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of these websites. Access to external sites is at your own risk.'
      },
      {
        title: '7. Limitation of Liability',
        content: 'Vega Robotics is not liable for direct, indirect, incidental, or consequential damages resulting from the use or inability to use the website. The website is provided "as is" without warranties of any kind.'
      },
      {
        title: '8. Modifications to Terms',
        content: 'We reserve the right to modify these Terms of Use at any time. Changes take effect immediately upon publication on the website. Continued use of the website after modifications constitutes acceptance of the new terms.'
      },
      {
        title: '9. Applicable Law',
        content: 'These Terms of Use are governed by the laws of the Federative Republic of Brazil. Any dispute shall be submitted to the jurisdiction of the Courts of Campinas, State of São Paulo.'
      },
      {
        title: '10. Contact',
        content: 'For questions about these Terms of Use, please contact us:'
      }
    ],
    contact: {
      company: 'Vega Robotics',
      email: 'contato@vegarobotics.com.br',
      address: 'Av. Alan Turing, 345 - Room 11, Campinas - SP, 13083-898, Brazil'
    }
  };

  // Conteúdo em espanhol
  const contentES = {
    title: 'Términos de Uso',
    lastUpdated: 'Última actualización: Diciembre de 2024',
    sections: [
      {
        title: '1. Aceptación de los Términos',
        content: 'Al acceder y utilizar el sitio web de Vega Robotics (vegarobotics.com.br) y sus subdominios, usted acepta estos Términos de Uso. Si no está de acuerdo con algún término, por favor no utilice nuestro sitio.'
      },
      {
        title: '2. Descripción de los Servicios',
        content: 'Vega Robotics ofrece soluciones de tecnología industrial, incluyendo:',
        list: [
          'Plataformas de Inteligencia Artificial Industrial',
          'Sistemas de Visión por Computadora',
          'Servicios de Ingeniería bajo Demanda (CADPro)',
          'Consultoría en Industria 4.0',
          'Agentes de IA para automatización de procesos (PROAgentes.ai)'
        ]
      },
      {
        title: '3. Uso del Sitio',
        content: 'Usted acepta utilizar este sitio solo para fines legales y de manera que no infrinja los derechos de terceros. Está prohibido:',
        list: [
          'Usar el sitio para cualquier propósito ilegal o no autorizado',
          'Intentar obtener acceso no autorizado a sistemas o redes',
          'Transmitir virus, malware o código malicioso',
          'Recopilar información de otros usuarios sin consentimiento',
          'Interferir con el funcionamiento del sitio'
        ]
      },
      {
        title: '4. Propiedad Intelectual',
        content: 'Todo el contenido del sitio, incluyendo textos, imágenes, logotipos, gráficos, videos y software, es propiedad de Vega Robotics o de sus licenciantes y está protegido por leyes de derechos de autor y propiedad intelectual. No se permite la reproducción, distribución o modificación sin autorización previa.'
      },
      {
        title: '5. Contenido del Blog',
        content: 'Los artículos publicados en nuestro blog tienen carácter informativo y educativo. Aunque nos esforzamos por proporcionar información precisa y actualizada, no garantizamos la completitud o exactitud del contenido. Las opiniones expresadas son responsabilidad de los autores.'
      },
      {
        title: '6. Enlaces a Sitios de Terceros',
        content: 'Nuestro sitio puede contener enlaces a sitios de terceros. No somos responsables del contenido, políticas de privacidad o prácticas de estos sitios. El acceso a sitios externos es bajo su propio riesgo.'
      },
      {
        title: '7. Limitación de Responsabilidad',
        content: 'Vega Robotics no se responsabiliza por daños directos, indirectos, incidentales o consecuentes resultantes del uso o imposibilidad de uso del sitio. El sitio se proporciona "tal cual", sin garantías de ningún tipo.'
      },
      {
        title: '8. Modificaciones en los Términos',
        content: 'Nos reservamos el derecho de modificar estos Términos de Uso en cualquier momento. Los cambios entran en vigor inmediatamente después de su publicación en el sitio. El uso continuado del sitio después de las modificaciones constituye aceptación de los nuevos términos.'
      },
      {
        title: '9. Ley Aplicable',
        content: 'Estos Términos de Uso se rigen por las leyes de la República Federativa del Brasil. Cualquier disputa será sometida a la jurisdicción de los Tribunales de Campinas, Estado de São Paulo.'
      },
      {
        title: '10. Contacto',
        content: 'Para preguntas sobre estos Términos de Uso, contáctenos:'
      }
    ],
    contact: {
      company: 'Vega Robotics',
      email: 'contato@vegarobotics.com.br',
      address: 'Av. Alan Turing, 345 - Sala 11, Campinas - SP, 13083-898, Brasil'
    }
  };

  // Seleciona conteúdo baseado no idioma
  const content = language === 'en' ? contentEN : language === 'es' ? contentES : contentPT;

  return (
    <Container>
      <Title>{content.title}</Title>
      <LastUpdated>{content.lastUpdated}</LastUpdated>

      {content.sections.map((section, index) => (
        <Section key={index}>
          <SectionTitle>{section.title}</SectionTitle>
          <Paragraph>{section.content}</Paragraph>
          {section.list && (
            <List>
              {section.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </List>
          )}
        </Section>
      ))}

      <ContactInfo>
        <Paragraph><strong>{content.contact.company}</strong></Paragraph>
        <Paragraph>
          E-mail: <a href={`mailto:${content.contact.email}`}>{content.contact.email}</a>
        </Paragraph>
        <Paragraph>{content.contact.address}</Paragraph>
      </ContactInfo>
    </Container>
  );
};

export default Terms;
